import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Terminal, Trash2, Send, Loader2 } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

export const TerminalPane = ({ 
  output = [], 
  onClear, 
  pendingInput,
  onSubmitInput,
  loading = false,
}) => {
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  // Auto-scroll to bottom when output changes
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [output]);

  // Focus input when pending
  useEffect(() => {
    if (pendingInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [pendingInput]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSubmitInput?.(inputValue);
      setInputValue('');
    }
  };

  const getOutputStyle = (type) => {
    switch (type) {
      case 'stdout':
        return 'text-terminal-text';
      case 'error':
        return 'text-destructive';
      case 'success':
        return 'text-success';
      case 'system':
        return 'text-primary';
      case 'code':
        return 'text-muted-foreground italic';
      case 'prompt':
        return 'text-secondary';
      case 'input':
        return 'text-accent';
      default:
        return 'text-foreground';
    }
  };

  return (
    <div className="flex flex-col h-full bg-terminal-bg rounded-xl overflow-hidden border border-border/20">
      {/* Terminal Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-editor-gutter border-b border-border/20">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-terminal-prompt" />
          <span className="text-sm font-medium text-foreground">פלט</span>
          <span className="text-xs text-muted-foreground">(Output)</span>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground hover:bg-muted/20 h-7 px-2"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Terminal Output */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4">
        <div className="font-mono text-sm space-y-1 terminal-output">
          {output.length === 0 ? (
            <div className="text-muted-foreground text-center py-8">
              <Terminal className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>הפלט יופיע כאן</p>
              <p className="text-xs mt-1">Output will appear here</p>
            </div>
          ) : (
            output.map((line, index) => (
              <div 
                key={index} 
                className={`${getOutputStyle(line.type)} animate-slide-up`}
                style={{ animationDelay: `${index * 20}ms` }}
              >
                {line.type === 'stdout' && (
                  <span className="text-terminal-prompt select-none">{'>>> '}</span>
                )}
                <span className="whitespace-pre-wrap break-words">{line.content}</span>
              </div>
            ))
          )}
          
          {/* Loading indicator */}
          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground animate-pulse">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>מריץ קוד...</span>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input area (when waiting for input) */}
      {pendingInput && (
        <form 
          onSubmit={handleSubmit}
          className="flex items-center gap-2 p-3 bg-editor-gutter border-t border-border/20"
        >
          <span className="text-terminal-prompt font-mono text-sm">{'>>> '}</span>
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={pendingInput}
            className="flex-1 bg-terminal-bg border-border/30 text-terminal-text font-mono text-sm h-8"
            dir="auto"
          />
          <Button
            type="submit"
            size="sm"
            className="bg-primary hover:bg-primary/90 h-8 px-3"
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </form>
      )}
    </div>
  );
};
